import { Link, useLocation } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import { useEffect, useRef, useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";

export default function Header({ variant = "landing" }) {
  const { settings } = useSettings();
  const loc = useLocation();
  const isLanding = loc.pathname === "/";
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const servicesRef = useRef(null);

  // Close dropdowns when route changes
  useEffect(() => {
    setServicesOpen(false);
    setMobileOpen(false);
  }, [loc.pathname]);

  // Close services on outside click / Escape
  useEffect(() => {
    const onClick = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setServicesOpen(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-4 flex items-center justify-between">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src={settings?.logo_url || "/assets/logo.png"}
            alt="DAS SIMAO AUTO"
            className="h-11 w-11 object-contain rounded-sm"
          />
          <div className="leading-tight hidden sm:block">
            <div className="text-white text-sm tracking-[0.28em] font-medium">
              DAS SIMAO
            </div>
            <div className="text-[10px] tracking-[0.4em] gold uppercase">
              Auto — À votre service
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6 text-sm text-gray-300">
          {!isLanding && (
            <Link
              to="/"
              data-testid="nav-home"
              className="hover:text-[color:var(--gold)] transition-colors"
            >
              Accueil
            </Link>
          )}

          <div className="relative" ref={servicesRef}>
            <button
              type="button"
              data-testid="nav-services"
              onClick={() => setServicesOpen((o) => !o)}
              aria-expanded={servicesOpen}
              aria-haspopup="menu"
              className={`inline-flex items-center gap-1 transition-colors focus:outline-none focus:text-[color:var(--gold)] ${
                variant === "convoyage" || variant === "vtc"
                  ? "text-[color:var(--gold)]"
                  : "hover:text-[color:var(--gold)]"
              }`}
            >
              Services <ChevronDown size={14} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            {servicesOpen && (
              <div
                role="menu"
                data-testid="nav-services-menu"
                className="absolute top-full right-0 mt-2 w-72 bg-[#0a0a0a] border border-white/10 shadow-2xl"
              >
                <MenuLink to="/services/convoyage" testId="nav-service-convoyage" title="Convoyage" subtitle="Livraison de votre véhicule" />
                <MenuLink to="/services/vtc" testId="nav-service-vtc" title="VTC" subtitle="Chauffeur privé & mise à disposition" />
                <MenuLink to="/services/nettoyage" testId="nav-service-nettoyage" title="Nettoyage" subtitle="Nettoyage & préparation esthétique" />
                <MenuLink to="/services/depannage" testId="nav-service-depannage" title="Dépannage" subtitle="Bientôt disponible" comingSoon />
              </div>
            )}
          </div>

          <Link to="/suivi" data-testid="nav-track" className="hover:text-[color:var(--gold)] transition-colors">
            Suivi
          </Link>
          <Link
            to="/admin"
            data-testid="nav-admin"
            className="text-xs uppercase tracking-widest text-gray-400 hover:gold transition-colors"
          >
            Admin
          </Link>
        </nav>

        {/* Mobile burger */}
        <button
          type="button"
          data-testid="nav-burger"
          onClick={() => setMobileOpen((o) => !o)}
          aria-expanded={mobileOpen}
          aria-label="Ouvrir le menu"
          className="md:hidden text-white p-2 -mr-2 focus:outline-none focus:text-[color:var(--gold)]"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu-panel"
          className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl"
        >
          <div className="px-5 py-4 flex flex-col divide-y divide-white/5">
            {!isLanding && (
              <MobileLink to="/" testId="mobile-nav-home" label="Accueil" />
            )}
            <div className="py-3">
              <div className="text-[10px] tracking-[0.4em] gold uppercase mb-2">Services</div>
              <div className="flex flex-col gap-1">
                <Link to="/services/convoyage" data-testid="mobile-nav-service-convoyage" className="py-2 text-white hover:gold">Convoyage</Link>
                <Link to="/services/vtc" data-testid="mobile-nav-service-vtc" className="py-2 text-white hover:gold">VTC</Link>
                <Link to="/services/nettoyage" data-testid="mobile-nav-service-nettoyage" className="py-2 text-white hover:gold">Nettoyage</Link>
                <Link to="/services/depannage" data-testid="mobile-nav-service-depannage" className="py-2 text-white hover:gold inline-flex items-center gap-2">
                  Dépannage
                  <span className="text-[9px] uppercase tracking-widest gold border border-[color:var(--gold)]/40 px-1.5 py-0.5">bientôt</span>
                </Link>
              </div>
            </div>
            <MobileLink to="/suivi" testId="mobile-nav-track" label="Suivi de réservation" />
            <MobileLink to="/admin" testId="mobile-nav-admin" label="Administration" muted />
          </div>
        </div>
      )}
    </header>
  );
}

function MenuLink({ to, testId, title, subtitle, comingSoon }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      role="menuitem"
      className="block px-5 py-4 border-b border-white/5 last:border-b-0 hover:bg-white/[0.03] focus:bg-white/[0.05] focus:outline-none transition-colors group"
    >
      <div className="flex items-center gap-2">
        <span className="text-white group-hover:gold transition-colors">{title}</span>
        {comingSoon && (
          <span className="text-[9px] uppercase tracking-widest gold border border-[color:var(--gold)]/40 px-1.5 py-0.5">
            bientôt
          </span>
        )}
      </div>
      <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>
    </Link>
  );
}

function MobileLink({ to, testId, label, muted }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`py-3 ${muted ? "text-gray-400 text-xs uppercase tracking-widest" : "text-white"}`}
    >
      {label}
    </Link>
  );
}
